import { ILegalClient } from '../Interfaces/ILegalClient';

/** Eine abgestufte Mandanten-Warnleiste (rot = error, orange = warning). */
export interface ClientWarning {
  severity: 'error' | 'warning';
  text: string;
}

/**
 * Anzeigename eines Mandanten ("Vorname Nachname", ohne führende Leerzeichen).
 * LastName wird defensiv abgesichert: bei unvollständigen Objekten (z. B. reine
 * Verknüpfungszeilen ohne Namensfelder) darf niemals "undefined" im Text landen.
 */
export const fullName = (client: ILegalClient) =>
  `${client.FirstName ?? ''} ${client.LastName ?? ''}`.trim();

/** Kommagetrennte Liste der Mandanten-Namen. */
export const joinNames = (clients: ILegalClient[]) =>
  clients.map(fullName).join(', ');

/** true, wenn der Mandant eine nicht-leere E-Mail-Adresse hinterlegt hat. */
export const hasEmail = (client: ILegalClient) =>
  !!client.Email && client.Email.trim() !== '';

/** true, wenn der Mandant sich im Mandantenportal anmelden kann. */
export const canAccessPortal = (client: ILegalClient) => client.loginPossible;

/**
 * Ableitbarer Grund für den fehlenden Portalzugang. Ein "gesperrt"-Zustand
 * liegt für Mandanten nicht in den Daten vor, daher nur diese beiden Gründe.
 */
export const portalBlockReason = (client: ILegalClient) =>
  client.idLegalClientUser === -1
    ? 'kein verknüpftes Benutzerkonto'
    : 'Portalzugang deaktiviert';

export interface ClientReachability {
  /** Keine E-Mail-Adresse → weder Portal-Login noch Benachrichtigung (rot). */
  noEmail: ILegalClient[];
  /** E-Mail da, aber kein Portalzugang → Dokumente nicht abrufbar (rot). */
  noPortal: ILegalClient[];
  /** E-Mail + Portal da, aber Empfang deaktiviert → keine Benachrichtigung (orange). */
  noNotification: ILegalClient[];
}

/**
 * Teilt Mandanten nach ihrer digitalen Erreichbarkeit auf. Zentralisiert die
 * Klassifikation, die sowohl die Zahlungsschuldner-Warnungen (#1185) als auch
 * die allgemeinen Mandanten-Warnungen (#1198) verwenden.
 *
 * Die drei Kategorien schließen sich gegenseitig aus: ein Mandant ohne E-Mail
 * zählt nur zu `noEmail`, ein Mandant ohne Portalzugang nur zu `noPortal`
 * (auch bei zusätzlich deaktiviertem Empfang).
 */
export const classifyClientReachability = (
  clients: ILegalClient[]
): ClientReachability => ({
  noEmail: clients.filter(client => !hasEmail(client)),
  noPortal: clients.filter(
    client => hasEmail(client) && !canAccessPortal(client)
  ),
  noNotification: clients.filter(
    client =>
      hasEmail(client) &&
      canAccessPortal(client) &&
      !client.isReceivingEmailEnabled
  ),
});
